// Auth endpoint functions. Async, call `authApi`, throw `AppError` (mapped
// by the interceptor), and return DTOs — parsers run in the mutation hooks.
// Each response is unwrapped from the auth-service `{ success, message, data }`
// envelope here so the rest of the app never sees the envelope.
//
// No UAE Pass secrets live on the client: client id, redirect URI, scope, acr,
// and the anti-CSRF state are all owned by the auth service. The frontend only
// asks for an authorize URL, redirects the browser to it, then posts back the
// returned code + state.

import { authApi } from '@core/api/httpClient'
import { authEndpointPaths } from './authEndpointPaths'
import type {
  ApiEnvelope,
  EmailLoginDto,
  VerifyOtpDto,
  ResendOtpDto,
  UaePassAuthorizeUrlDto,
  UaePassCallbackDto,
  UaePassLogoutUrlDto,
  UaePassRegisterDto,
  LogoutResponseDto,
} from './authContracts'

// Starts email sign-in: the service mails a one-time code and returns the
// verification id that binds it. `portalCode` scopes the lookup to the seller
// portal — the same email on another portal is a different account.
export async function requestEmailOtp(payload: {
  portalCode: string
  email: string
}): Promise<EmailLoginDto> {
  const { data } = await authApi.post<ApiEnvelope<EmailLoginDto>>(
    authEndpointPaths.EMAIL_LOGIN,
    payload,
  )
  return data.data
}

// Redeems the code against the verification id and issues the session. The
// email is never re-sent here — the id is the only handle, so a tampered client
// cannot swap identities between requesting and verifying.
export async function verifyEmailOtp(payload: {
  verificationId: string
  otp: string
}): Promise<VerifyOtpDto> {
  const { data } = await authApi.post<ApiEnvelope<VerifyOtpDto>>(
    authEndpointPaths.VERIFY_OTP,
    payload,
  )
  return data.data
}

export async function resendEmailOtp(payload: { verificationId: string }): Promise<ResendOtpDto> {
  const { data } = await authApi.post<ApiEnvelope<ResendOtpDto>>(
    authEndpointPaths.RESEND_OTP,
    payload,
  )
  return data.data
}

export async function getUaePassAuthorizeUrl(portalCode: string): Promise<UaePassAuthorizeUrlDto> {
  const { data } = await authApi.get<ApiEnvelope<UaePassAuthorizeUrlDto>>(
    authEndpointPaths.UAE_PASS_AUTHORIZE_URL,
    { params: { portalCode } },
  )
  return data.data
}

// Front-channel UAE Pass single-logout URL for a login that never produced a
// session. Public on the auth service (there is no token to send) because a
// failed callback still leaves the UAE Pass SSO cookie in place — without
// clearing it, the next sign-in re-approves the same identity silently and the
// user is stuck on the same error.
export async function getUaePassLogoutUrl(portalCode: string): Promise<UaePassLogoutUrlDto> {
  const { data } = await authApi.get<ApiEnvelope<UaePassLogoutUrlDto>>(
    authEndpointPaths.UAE_PASS_LOGOUT_URL,
    { params: { portalCode } },
  )
  return data.data
}

export async function exchangeUaePassCode(payload: {
  code: string
  state: string
}): Promise<UaePassCallbackDto> {
  const { data } = await authApi.post<ApiEnvelope<UaePassCallbackDto>>(
    authEndpointPaths.UAE_PASS_CALLBACK,
    payload,
  )
  return data.data
}

export async function registerWithUaePass(payload: {
  signupTicket: string
  mobileCountryCode: string
  mobile: string
}): Promise<UaePassRegisterDto> {
  const { data } = await authApi.post<ApiEnvelope<UaePassRegisterDto>>(
    authEndpointPaths.UAE_PASS_REGISTER,
    payload,
  )
  return data.data
}

// Invalidates the server session. Must run while the access token is still
// present (authApi injects the Bearer), so the caller logs out server-side
// BEFORE clearing local tokens. Returns the UAE Pass single-logout URL when the
// session was a UAE Pass login.
export async function logout(payload: { refreshToken: string }): Promise<LogoutResponseDto> {
  const { data } = await authApi.post<ApiEnvelope<LogoutResponseDto>>(
    authEndpointPaths.LOGOUT,
    payload,
  )
  return data.data
}
